/**
 * Saisonkalender / Newsletter Popup with Klaviyo Double Opt-In
 *
 * Flow:
 *  1. User fills first_name + email and picks one of two radios:
 *     - "new"      → not yet a newsletter subscriber
 *     - "existing" → already subscribed
 *  2. Frontend always calls Klaviyo Client Subscriptions API:
 *     - New profile → Klaviyo sends DOI confirmation email.
 *     - Already-confirmed profile → no-op.
 *  3. Only when the user picked "existing", the frontend tracks a Klaviyo custom
 *     event (default: "Saisonkalender angefordert") with `subscriber_status`,
 *     `source`, `download_name`, `download_url`. New subscribers do not fire the
 *     event — they receive the asset via the post-DOI Welcome flow instead.
 *  4. Klaviyo flows deliver the asset:
 *     - Welcome Flow (trigger: "Subscribed to List X") fires for new subscribers
 *       after DOI confirmation — extended to include the Saisonkalender for profiles
 *       whose source is "Saisonkalender Popup".
 *     - Saisonkalender Flow (trigger: custom event, filter: "is in List X AND consented")
 *       fires immediately for already-confirmed subscribers.
 *
 *  If a user picks "existing" but is not actually subscribed, the subscription call
 *  still triggers DOI, and the event-based flow filter excludes them — so the DOI
 *  process cannot be bypassed.
 */
document.addEventListener('DOMContentLoaded', () => {
  const modalTriggers = document.querySelectorAll('[data-modal-trigger^="download-doi-modal-"]');
  const modalCloseButtons = document.querySelectorAll('[data-modal-close^="download-doi-modal-"]');
  const downloadForms = document.querySelectorAll('.js-download-doi-form');

  modalTriggers.forEach(trigger => {
    trigger.addEventListener('click', e => {
      e.preventDefault();
      const modalId = trigger.getAttribute('data-modal-trigger');
      const modal = document.getElementById(modalId);
      if (modal) {
        modal.classList.remove('hidden');
        modal.classList.add('flex', 'items-center', 'justify-center');
        const firstNameInput = modal.querySelector('input[name="first_name"]');
        if (firstNameInput) {
          setTimeout(() => firstNameInput.focus(), 100);
        }
        document.body.style.overflow = 'hidden';
      }
    });
  });

  modalCloseButtons.forEach(closeBtn => {
    closeBtn.addEventListener('click', e => {
      e.preventDefault();
      const modalId = closeBtn.getAttribute('data-modal-close');
      const modal = document.getElementById(modalId);
      if (modal) closeModal(modal);
    });
  });

  document.addEventListener('click', e => {
    if (
      e.target.id &&
      e.target.id.startsWith('download-doi-modal-') &&
      e.target.classList.contains('fixed')
    ) {
      closeModal(e.target);
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      const openModal = document.querySelector('[id^="download-doi-modal-"]:not(.hidden)');
      if (openModal) closeModal(openModal);
    }
  });

  downloadForms.forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      await handleFormSubmission(form);
    });
  });

  function closeModal(modal) {
    modal.classList.add('hidden');
    modal.classList.remove('flex', 'items-center', 'justify-center');
    document.body.style.overflow = '';

    const form = modal.querySelector('.js-download-doi-form');
    if (form) {
      form.reset();
      hideMessages(form);
    }
  }

  function hideMessages(form) {
    const successMsg = form.querySelector('.js-success-message');
    const errorMsg = form.querySelector('.js-error-message');
    form.querySelectorAll('input').forEach(input => {
      input.setAttribute('aria-invalid', 'false');
      input.classList.remove('text-erdbeere');
    });

    if (successMsg) successMsg.classList.add('hidden');
    if (errorMsg) errorMsg.classList.add('hidden');
  }

  function showError(form, message, focusInput) {
    const errorMsg = form.querySelector('.js-error-message');
    const errorText = form.querySelector('.js-error-text');
    const successMsg = form.querySelector('.js-success-message');

    if (successMsg) successMsg.classList.add('hidden');
    if (errorMsg) {
      if (errorText) errorText.textContent = message;
      errorMsg.classList.remove('hidden');
    }
    if (focusInput) {
      focusInput.setAttribute('aria-invalid', 'true');
      focusInput.classList.add('text-erdbeere');
      focusInput.focus();
    }
  }

  function showSuccess(form) {
    const successMsg = form.querySelector('.js-success-message');
    const errorMsg = form.querySelector('.js-error-message');

    if (errorMsg) errorMsg.classList.add('hidden');
    if (successMsg) successMsg.classList.remove('hidden');
    form.querySelectorAll('input').forEach(input => {
      input.setAttribute('aria-invalid', 'false');
      input.classList.remove('text-erdbeere');
    });
  }

  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Subscribe to the Klaviyo list. With DOI enabled on the list, new profiles
   * receive a confirmation email; already-confirmed profiles are a no-op.
   * Docs: https://developers.klaviyo.com/en/reference/create_client_subscription
   */
  async function subscribeToKlaviyo(email, firstName, companyId, listId, customSource) {
    const profileAttributes = { email };
    if (firstName) profileAttributes.first_name = firstName;

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: customSource || 'Saisonkalender Popup',
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          },
        }),
      }
    );

    if (response.status === 202) return;

    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.errors && errorData.errors[0] && errorData.errors[0].detail;
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }

  /**
   * Track a Klaviyo custom event. This is what triggers the Saisonkalender flow
   * (immediate send for already-confirmed subscribers, filtered by "is in list").
   * Docs: https://developers.klaviyo.com/en/reference/create_client_event
   */
  async function trackKlaviyoEvent(email, firstName, companyId, eventName, properties) {
    const profileAttributes = { email };
    if (firstName) profileAttributes.first_name = firstName;

    const response = await fetch(
      `https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: properties || {},
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: eventName },
                },
              },
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
            },
          },
        }),
      }
    );

    if (response.status === 202) return;

    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.errors && errorData.errors[0] && errorData.errors[0].detail;
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }

  async function handleFormSubmission(form) {
    const firstNameInput = form.querySelector('input[name="first_name"]');
    const emailInput = form.querySelector('input[name="email"]');
    const submitButton = form.querySelector('button[type="submit"]');
    const subscriberStatusInput = form.querySelector('input[name="subscriber_status"]:checked');
    const companyId = form.getAttribute('data-klaviyo-company-id');
    const listId = form.getAttribute('data-klaviyo-list-id');
    const downloadName = form.getAttribute('data-download-name') || '';
    const downloadUrl = form.getAttribute('data-download-url') || '';
    const eventName = form.getAttribute('data-event-name') || 'Saisonkalender angefordert';
    const source = form.getAttribute('data-source') || 'Saisonkalender Popup';

    const firstName = firstNameInput ? firstNameInput.value.trim() : '';
    const email = emailInput.value.trim();
    const subscriberStatus = subscriberStatusInput ? subscriberStatusInput.value : '';

    hideMessages(form);

    if (!firstName) {
      showError(form, 'Bitte gib deinen Vornamen ein.', firstNameInput);
      return;
    }

    if (!email) {
      showError(form, 'Bitte gib deine E-Mail-Adresse ein.', emailInput);
      return;
    }

    if (!isValidEmail(email)) {
      showError(form, 'Bitte gib eine gültige E-Mail-Adresse ein.', emailInput);
      return;
    }

    if (!subscriberStatus) {
      showError(form, 'Bitte wähle eine der beiden Optionen aus.');
      return;
    }

    if (!companyId || !listId) {
      showError(form, 'Klaviyo-Konfiguration fehlt. Bitte kontaktiere den Administrator.');
      return;
    }

    submitButton.disabled = true;
    submitButton.classList.add('opacity-50', 'cursor-not-allowed');

    try {
      // 1. Subscribe (handles DOI for new profiles, no-op for confirmed ones).
      //    Even if the user picked "existing", a non-subscribed email still triggers
      //    DOI here — the DOI process cannot be bypassed via checkbox choice.
      await subscribeToKlaviyo(email, firstName, companyId, listId, source);

      // 2. Track event only for self-reported existing subscribers, so the
      //    Saisonkalender flow fires immediately for them. New subscribers receive
      //    the asset via the post-DOI Welcome flow and do not need this event.
      //    Best-effort: if this fails, the asset is still deliverable via Welcome.
      if (subscriberStatus === 'existing') {
        try {
          await trackKlaviyoEvent(email, firstName, companyId, eventName, {
            download_name: downloadName,
            download_url: downloadUrl,
            source: source,
            subscriber_status: subscriberStatus,
          });
        } catch (eventError) {
          console.warn('[article-download-doi] Klaviyo event tracking failed:', eventError);
        }
      }

      showSuccess(form);

      try {
        localStorage.setItem('article-download-doi-email', email);
      } catch (e) {
        // localStorage may be unavailable; ignore.
      }

      // Auto-close after a short delay so the user reads the success message.
      const modal = form.closest('[id^="download-doi-modal-"]');
      if (modal) {
        setTimeout(() => closeModal(modal), 4000);
      }
    } catch (error) {
      console.error('[article-download-doi] Klaviyo subscription failed:', error);
      let errorMessage = 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.';

      if (error.message.includes('HTTP error')) {
        if (error.message.includes('400')) {
          errorMessage = 'Ungültige E-Mail-Adresse oder fehlende Klaviyo-Konfiguration.';
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Ungültige Klaviyo Company ID. Bitte kontaktiere den Administrator.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Klaviyo Liste nicht gefunden. Bitte kontaktiere den Administrator.';
        }
      }

      showError(form, errorMessage, emailInput);
    } finally {
      submitButton.disabled = false;
      submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
    }
  }
});
